/**
 * The guard behind every clickable table row (UI spec §3.5).
 *
 * A row that opens something is a pointer convenience layered over a real link
 * inside it, never a replacement for it: a `<tr>` is not focusable and carries no
 * role that would make a click handler reachable by keyboard, so each table keeps
 * an anchor in one of its cells as the keyboard and assistive-technology route to
 * the same target (R18.6). This module holds the part both tables would otherwise
 * duplicate — deciding whether a given click is actually a plain activation of the
 * row — so the Initiative list and the Demand Explorer cannot drift into two
 * different ideas of when a row opens.
 */
import type { MouseEvent } from 'react';

/**
 * Whether this click should open the row's target, deferring to the browser and to
 * nested controls wherever a plain navigation would be the wrong answer:
 *
 * - A click that landed on a link, button or form control is that control's. The
 *   row's anchor holds the same target, so without this guard clicking it would
 *   both follow the link and run the row handler; other cells may gain controls
 *   later.
 * - A modified click (Cmd/Ctrl/Shift/Alt) or a non-primary button is the user
 *   asking for a new tab, a new window or a context menu. A `navigate` call would
 *   replace the current view instead, so those are left to the anchor and the
 *   browser.
 * - A click that ends a text selection is a selection, not an activation. Reading
 *   a title out of a row and copying it should not navigate away from the table.
 */
export function isPlainRowActivation(event: MouseEvent<HTMLElement>): boolean {
  if (event.defaultPrevented || event.button !== 0) {
    return false;
  }
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  if (
    event.target instanceof Element &&
    event.target.closest('a, button, input, select, textarea, [role="button"]') !== null
  ) {
    return false;
  }
  if ((window.getSelection()?.toString() ?? '') !== '') {
    return false;
  }
  return true;
}
